import React, { useState, useEffect } from "react";
import { config } from "../../config/config";
import mediaService from "../../services/mediaService";

function HeroSectionTab() {
  const [heroData, setHeroData] = useState({
    headline: "Become a better leader, transform your business",
    subheadline: "Turn people into assets, drive growth with innovation",
    cta_text: "Contact us for details",
    background_image: "",
    slider_images: [],
    use_slider: false,
    slider_interval: 5000,
  });

  const [saving, setSaving] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [selectedImageType, setSelectedImageType] = useState(null);
  const [previewSlides, setPreviewSlides] = useState(0);

  useEffect(() => {
    loadHeroData();
  }, []);

  const loadHeroData = async () => {
    try {
      const response = await fetch(`${config.api.baseURL}/homepage/hero`);
      if (response.ok) {
        const data = await response.json();
        // Convert stored relative paths to absolute URLs for preview
        const converted = { ...data };
        if (converted.background_image) {
          converted.background_image = mediaService.getFileUrl(
            converted.background_image,
          );
        }
        if (Array.isArray(converted.slider_images)) {
          converted.slider_images = converted.slider_images.map((p) =>
            mediaService.getFileUrl(p),
          );
        }
        setHeroData(converted);
      }
    } catch (error) {
      console.error("Error loading hero data:", error);
    }
  };

  const loadMediaLibrary = async () => {
    try {
      const media = await mediaService.getMediaList("hero");
      setMediaLibrary(Array.isArray(media) ? media : []);
      setShowMediaLibrary(true);
    } catch (error) {
      console.error("Error loading media library:", error);
      alert("Failed to load media library");
    }
  };

  const handleSelectImage = (imagePath) => {
    const fullUrl = mediaService.getFileUrl(
      imagePath.path || imagePath.url || imagePath.file_path,
    );

    if (selectedImageType === "background") {
      setHeroData({ ...heroData, background_image: fullUrl });
    } else if (selectedImageType === "slider") {
      const newSlides = [...(heroData.slider_images || [])];
      if (!newSlides.includes(fullUrl)) {
        newSlides.push(fullUrl);
        setHeroData({ ...heroData, slider_images: newSlides });
      }
    }

    setShowMediaLibrary(false);
    setSelectedImageType(null);
  };

  const removeSliderImage = (index) => {
    const newSlides = heroData.slider_images.filter((_, i) => i !== index);
    setHeroData({ ...heroData, slider_images: newSlides });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${config.api.baseURL}/homepage/hero`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(heroData),
      });

      if (response.ok) {
        alert("Hero section updated successfully!");
      } else {
        throw new Error("Failed to update hero section");
      }
    } catch (error) {
      console.error("Error updating hero section:", error);
      alert("Failed to update hero section. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-6">Edit Hero Section</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Text Content */}
          <div className="space-y-4 pb-6 border-b">
            <h3 className="font-medium text-gray-900">Text Content</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Headline
              </label>
              <input
                type="text"
                value={heroData.headline || ""}
                onChange={(e) =>
                  setHeroData({ ...heroData, headline: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter hero headline"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subheadline
              </label>
              <textarea
                value={heroData.subheadline || ""}
                onChange={(e) =>
                  setHeroData({ ...heroData, subheadline: e.target.value })
                }
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter hero subheadline"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CTA Button Text
              </label>
              <input
                type="text"
                value={heroData.cta_text || ""}
                onChange={(e) =>
                  setHeroData({ ...heroData, cta_text: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter call-to-action text"
              />
            </div>
          </div>

          {/* Background Image */}
          <div className="space-y-4 pb-6 border-b">
            <h3 className="font-medium text-gray-900">Background Image</h3>

            {heroData.background_image && (
              <div className="relative">
                <img
                  src={heroData.background_image}
                  alt="Background preview"
                  className="w-full h-40 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() =>
                    setHeroData({ ...heroData, background_image: "" })
                  }
                  className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setSelectedImageType("background");
                loadMediaLibrary();
              }}
              className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition"
            >
              {heroData.background_image
                ? "Change Background Image"
                : "Select Background Image"}
            </button>
          </div>

          {/* Slider Settings */}
          <div className="space-y-4 pb-6 border-b">
            <h3 className="font-medium text-gray-900">Image Slider</h3>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="use_slider"
                checked={heroData.use_slider || false}
                onChange={(e) =>
                  setHeroData({ ...heroData, use_slider: e.target.checked })
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                htmlFor="use_slider"
                className="ml-3 text-sm font-medium text-gray-700"
              >
                Enable image slider in hero section
              </label>
            </div>

            {heroData.use_slider && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slider Interval (milliseconds)
                </label>
                <input
                  type="number"
                  value={heroData.slider_interval || 5000}
                  onChange={(e) =>
                    setHeroData({
                      ...heroData,
                      slider_interval: parseInt(e.target.value),
                    })
                  }
                  min="1000"
                  step="1000"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Time between slides in milliseconds
                </p>
              </div>
            )}

            {/* Slider Images */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Slider Images ({heroData.slider_images?.length || 0})
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedImageType("slider");
                    loadMediaLibrary();
                  }}
                  disabled={!heroData.use_slider}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  + Add Image
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {heroData.slider_images?.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`Slider ${index + 1}`}
                      className="w-full aspect-video object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeSliderImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    >
                      ✕
                    </button>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Slide {index + 1}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6 border-t">
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>

        {/* Preview */}
        <div className="mt-8 pt-6 border-t">
          <h3 className="font-semibold mb-4">Preview</h3>
          <div
            className="relative bg-gradient-to-r from-[#1F3A5F] to-[#3B82F6] text-white p-12 rounded-lg overflow-hidden"
            style={{
              backgroundImage: heroData.background_image
                ? `url(${heroData.background_image})`
                : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {/* Dark overlay for text readability */}
            {heroData.background_image && (
              <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            )}

            <div className="relative z-10">
              <h1 className="text-3xl font-bold mb-3">
                {heroData.headline || "Headline"}
              </h1>
              <p className="text-lg mb-6 max-w-2xl">
                {heroData.subheadline || "Subheadline"}
              </p>
              <button className="bg-white text-blue-600 px-6 py-2 rounded-full font-semibold hover:bg-gray-100 transition">
                {heroData.cta_text || "Call to Action"}
              </button>
            </div>

            {/* Slider Indicator */}
            {heroData.use_slider && heroData.slider_images?.length > 0 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                {heroData.slider_images.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setPreviewSlides(index)}
                    className={`w-2 h-2 rounded-full transition ${
                      previewSlides === index
                        ? "bg-white"
                        : "bg-white bg-opacity-50"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h3 className="font-semibold">Select Image</h3>
              <button
                onClick={() => setShowMediaLibrary(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="p-4">
              {mediaLibrary.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No media files found
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {mediaLibrary.map((media, index) => {
                    const fullUrl = mediaService.getFileUrl(
                      media.path || media.url || media.file_path,
                    );
                    return (
                      <button
                        key={index}
                        onClick={() => handleSelectImage(media)}
                        className="relative group"
                      >
                        <img
                          src={fullUrl}
                          alt={media.name || media.filename}
                          className="w-full aspect-square object-cover rounded-lg border-2 border-transparent group-hover:border-blue-500 transition"
                        />
                        <p className="text-xs text-gray-600 mt-1 truncate">
                          {media.name || media.filename}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HeroSectionTab;
